import { isDefined } from "@omni/utils/helper.utils";
import { cn } from "@omni/utils/styles";
import { useEffect, useRef, useState } from "react";

import { useSearchResultsContext } from "@/components/Search/SearchResults/context/searchResults.context";
import {
    MobileProductFacets,
    ProductFacets
} from "@/components/Search/SearchResults/StateComponents/Facets/facetsComponents";
import { ProductHitsPerPage } from "@/components/Search/SearchResults/StateComponents/HitsPerPage/hitsPerPageComponents";
import { SEARCH_PRODUCT_ID } from "@/components/Search/SearchResults/StateComponents/NumberOfHits/numberOfHits.container";
import { ProductPagination } from "@/components/Search/SearchResults/StateComponents/Pagination/paginationComponents";
import { ProductResultList } from "@/components/Search/SearchResults/StateComponents/ResultList/resultListComponents";
import { SelectedFacets } from "@/components/Search/SearchResults/StateComponents/SelectedFacets/selectedFacets.container";
import { ProductSort } from "@/components/Search/SearchResults/StateComponents/Sort/sortComponents";
import { useThunkDispatch } from "@/redux/dispatcher";
import * as CartModule from "@/redux/modules/api/data/cart/cart.module";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { blurActiveElement } from "@/utils/blur";
import { useBreakpoint } from "@/utils/breakpoints/breakpoints.provider";

import { AvailableInStore } from "./components/AvailableInStore/availableInStore.component";
import { OpenFiltersButton } from "./components/OpenFiltersButton/openFiltersButton.component";

const ProductSearchResultsComponent = () => {
    const { showFacets } = useSearchResultsContext();

    const { t } = useGranite();
    const dispatch = useThunkDispatch();

    const { breakpoints } = useBreakpoint();
    const isMobile = !breakpoints.lg;
    const [mobileFacetsOpen, setMobileFacetsOpen] = useState(false);

    const filtersRef = useRef<HTMLDivElement>(null);
    const stickyFiltersRef = useRef<HTMLDivElement>(null);
    const stickFilters = useRef<boolean>(false);

    const handleOnCloseMobileProductFacets = () =>
        setMobileFacetsOpen(() => false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        dispatch(CartModule.load());
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        const handleScroll = () => {
            if (!filtersRef.current) return;
            if (!stickyFiltersRef.current) return;

            const filtersY = filtersRef.current?.getBoundingClientRect().y;

            const stickFiltersToTop = isDefined(filtersY) && filtersY <= 0;

            if (stickFilters.current !== stickFiltersToTop) {
                if (stickFiltersToTop) {
                    blurActiveElement();
                    stickyFiltersRef.current.classList.remove("tw:hidden");
                    stickyFiltersRef.current.classList.add("tw:grid");
                } else {
                    stickyFiltersRef.current.classList.remove("tw:grid");
                    stickyFiltersRef.current.classList.add("tw:hidden");
                }

                stickFilters.current = stickFiltersToTop;
            }
        };

        document.addEventListener("scroll", handleScroll);

        return () => {
            document.removeEventListener("scroll", handleScroll);
        };
    }, [filtersRef, stickFilters]);

    return (
        <>
            {isMobile && showFacets && (
                <>
                    <div
                        className={cn(
                            "tw:grid-cols-[1fr_1fr] tw:gap-5 tw:top-0 tw:left-0 tw:bg-white tw:px-[1.4%] tw:layer-overlay/[0] tw:py-2 tw:border-b tw:border-b-grey-light tw:fixed tw:w-full tw:items-center tw:hidden"
                        )}
                        ref={stickyFiltersRef}
                    >
                        <div className={cn("tw:w-full")}>
                            <OpenFiltersButton
                                className={cn("tw:w-full!", "tw:lg:hidden")}
                                setOpen={setMobileFacetsOpen}
                            />
                        </div>
                        <div className={cn("tw:w-full")}>
                            <ProductSort className={cn("tw:w-full!")} />
                        </div>
                    </div>
                    <MobileProductFacets
                        isOpen={mobileFacetsOpen}
                        onClose={handleOnCloseMobileProductFacets}
                        className={cn("tw:lg:hidden")}
                    />
                </>
            )}
            {!isMobile && showFacets && (
                <section
                    className={cn(
                        "tw:min-w-full tw:hidden tw:row-[1/4]",
                        "tw:lg:block"
                    )}
                >
                    <ProductFacets />
                </section>
            )}
            <article
                id={SEARCH_PRODUCT_ID}
                className={cn("tw:col-[1/3]", "tw:lg:col-[2/3]", {
                    "tw:lg:col-1!": !showFacets
                })}
            >
                {showFacets && (
                    <header
                        className={cn(
                            "tw:mb-3 tw:justify-between tw:items-center",
                            "tw:sm:flex"
                        )}
                    >
                        <h3 className={"m-0"}>
                            {t("search.results.title.products")}
                        </h3>
                        <div
                            ref={filtersRef}
                            className={cn(
                                "tw:grid tw:grid-cols-[1fr_1fr] tw:gap-5 tw:justify-between tw:items-stretch tw:py-2",
                                "tw:sm:flex tw:sm:grid-cols-none tw:sm:gap-0",
                                "tw:md:justify-end"
                            )}
                        >
                            {isMobile && (
                                <div className={cn("tw:lg:hidden")}>
                                    <OpenFiltersButton
                                        className={"w-100"}
                                        setOpen={setMobileFacetsOpen}
                                    />
                                </div>
                            )}
                            <div
                                className={cn(
                                    "tw:hidden tw:items-center",
                                    "tw:not-first:ml-4 tw:not-first:pl-4 tw:not-first:border-l tw:not-first:border-divider",
                                    "tw:md:flex "
                                )}
                            >
                                <ProductHitsPerPage />
                            </div>
                            <div
                                className={cn(
                                    "tw:not-first:ml-4 tw:not-first:pl-4 tw:not-first:border-l tw:not-first:border-divider"
                                )}
                            >
                                <ProductSort
                                    className={cn({
                                        "tw:col-[2/3] tw:sm:col-[unset]":
                                            isMobile
                                    })}
                                />
                            </div>
                            {!isMobile && (
                                <div
                                    className={cn(
                                        "tw:flex tw:items-center tw:not-first:ml-4 tw:not-first:pl-4 tw:not-first:border-l tw:not-first:border-divider"
                                    )}
                                >
                                    <AvailableInStore
                                        className={cn("tw:pl-0!")}
                                    />
                                </div>
                            )}
                        </div>
                    </header>
                )}

                <SelectedFacets
                    component={"PRODUCT_FACETS"}
                    className={cn("tw:mb-3")}
                />
                <ProductResultList className={cn("tw:mb-3")} />
                <ProductPagination className={cn("tw:mb-3")} />
            </article>
        </>
    );
};

export { ProductSearchResultsComponent };
